import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { useWebAppGetYoutubeIntegrationStatsSelfViewers } from '@mi-front/api-v2';
import { date } from '@mi-front/core';
import { useEffect, useMemo, useReducer } from 'react';
import { YoutubeLiveCounterDisplay } from './YoutubeLiveCounterDisplay';

/**
 * Live viewer chip backed by web-app-bff `GET /youtube-stats/viewers/:station/self`.
 */
export function YoutubeLiveCounter() {
  const station = useActiveStation();
  const youtubeStatsEnabled = useYoutubeStatsEnabled(station);

  /** Bumps once per minute so `from`/`to` stay stable between refetches (required for Kubb query keys). */
  const [minuteEpoch, bumpMinuteEpoch] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const id = window.setInterval(() => bumpMinuteEpoch(), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { from, to } = useMemo(() => {
    const toMoment = date().startOf('minute');
    return {
      from: toMoment.clone().subtract(2, 'minutes').toISOString(),
      to: toMoment.toISOString(),
    };
  }, [minuteEpoch]);

  const { data, isLoading } = useWebAppGetYoutubeIntegrationStatsSelfViewers(
    station.id,
    { from, to },
    {
      query: {
        enabled: youtubeStatsEnabled && Boolean(station.id),
        refetchInterval: 60_000,
        staleTime: 60_000,
      },
    },
  );

  const { viewers, isOffline, time } = useMemo(() => {
    const series = data?.timeSeries;
    const last = series?.length ? series[series.length - 1] : undefined;
    const channelId = station.youtubeChannelId;
    const raw = channelId && last ? last[channelId] : undefined;
    const count = typeof raw === 'number' && Number.isFinite(raw) && !Number.isNaN(raw) ? raw : 0;
    const offline =
      !isLoading && (!last || !channelId || typeof raw !== 'number' || !Number.isFinite(raw));
    const rawTime = last?.time;
    const resolvedTime =
      !offline && (typeof rawTime === 'string' || typeof rawTime === 'number') ? rawTime : null;

    return { viewers: count, isOffline: offline, time: resolvedTime };
  }, [data?.timeSeries, isLoading, station.youtubeChannelId]);

  return youtubeStatsEnabled && !isLoading ? (
    <YoutubeLiveCounterDisplay viewers={viewers} isOffline={isOffline} time={time} />
  ) : null;
}
